import React, { useEffect, useState, useRef } from "react";
import { Modal, Button, Form, Tabs, Tab, Nav, Row, Col } from "react-bootstrap";
import { API_URL } from '../../../environment';
import { getSupabaseRealtimeCRM } from "../../../supabaseRealtime";
import "../../application/DocsRetencao.css";

interface CardViewModalProps {
  show: boolean;
  onHide: () => void;
  leadPhone: string;
  leadName: string;
  initialFunnelName: string;
  /** NÃO usada no rodapé: o card mostra "Salvar" em TODAS as telas. A promessa é
   *  registrada pelo botão "Promessa" do header do /chat. Prop mantida só para não
   *  quebrar quem ainda a passa (chat.tsx). */
  onRegistrarPromessa?: (pendentes: string[], recebidos: number) => void;
  /** id_time do LEAD. Só faz diferença para o admin master, que enxerga leads de
   *  outros times: sem isto o card lê e GRAVA no time do login, e o valor some da
   *  vista de quem loga no time real do lead. Omitido = time do login (comportamento
   *  de sempre), que é o correto para atendente e admin de time. */
  idTime?: string | number | null;
}

interface Funnel {
  id: number;
  nome: string;
}

interface Group {
  id: number;
  nome: string;
  ordem: number;
  padrao?: boolean;
  fields?: Field[];
}

interface Field {
  id: number;
  id_grupo: number;
  label: string;
  tipo: string;
  opcoes?: string;
  obrigatorio: boolean;
  ordem: number;
}

interface CardValue {
  id_campo: number;
  valor: string;
}

const CardViewModal: React.FC<CardViewModalProps> = ({ show, onHide, leadPhone, leadName, initialFunnelName, onRegistrarPromessa, idTime }) => {
  // Time dono do card. Fonte única para leitura, escrita, cache e realtime — se
  // divergirem, o card lê de um time e grava em outro.
  const timeDoCard = () => (idTime != null && String(idTime) ? String(idTime) : localStorage.getItem("id_cliente"));
  const [funnels, setFunnels] = useState<Funnel[]>([]);
  const [selectedFunnel, setSelectedFunnel] = useState<number | null>(null);
  const [groups, setGroups] = useState<Group[]>([]);
  const [values, setValues] = useState<CardValue[]>([]);
  const [loading, setLoading] = useState(false);
  const [fillingMode, setFillingMode] = useState<'name' | 'phone' | null>(null);
  // Campo de texto aberto em tela cheia (ver/editar o conteúdo completo)
  const [expandedField, setExpandedField] = useState<Field | null>(null);
  // Campo em modo de edição inline (clicou no valor para editar). null = ninguém editando.
  const [editingField, setEditingField] = useState<number | null>(null);
  // Campos que o usuário tocou nesta abertura do card. Carregamentos do servidor
  // (cache/fetch/auto-fill) NÃO sobrescrevem esses — evita apagar o que está sendo
  // digitado quando o fetch volta depois (race condition).
  const dirtyFields = useRef<Set<number>>(new Set());

  // Aplica valores vindos do servidor preservando o que o usuário está editando.
  const mergePreservandoDirty = (servidor: CardValue[]) => {
    setValues((prev) => {
      if (dirtyFields.current.size === 0) return servidor;
      const out = servidor.map((sv) =>
        dirtyFields.current.has(sv.id_campo)
          ? (prev.find((p) => p.id_campo === sv.id_campo) ?? sv)
          : sv
      );
      // mantém os campos editados que o servidor ainda não tem
      prev.forEach((p) => {
        if (dirtyFields.current.has(p.id_campo) && !out.some((o) => o.id_campo === p.id_campo)) {
          out.push(p);
        }
      });
      return out;
    });
  };

  useEffect(() => {
    if (show) {
      dirtyFields.current.clear(); // novo card / reabertura = começa limpo
      // Limpa os valores do lead ANTERIOR antes de carregar os do atual. Sem isso, se o
      // fetch de /card/values demora ou falha (res.ok=false), o card segue mostrando as
      // infos do lead anterior — exatamente o bug ao abrir um card, fechar e abrir outro.
      setValues([]);
      setExpandedField(null);
      setEditingField(null);
      fetchFunnels();
      fetchValues();
      setFillingMode(null);
    }
  }, [show, leadPhone, idTime]);

  // Realtime dos cards (card_valores @ banco CRM): edição de OUTRO atendente no mesmo
  // card aparece na hora. NÃO sobrescreve o campo que VOCÊ está editando (dirtyFields).
  useEffect(() => {
    if (!show) return;
    const supabase = getSupabaseRealtimeCRM();
    if (!supabase) return;
    const id_cliente = timeDoCard(); // é o id_time → coluna card_valores.id_time
    if (!id_cliente) return;
    const foneLead = String(leadPhone || "").replace(/\D/g, "");
    const channel = supabase
      .channel(`rt-card-${id_cliente}-${foneLead}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "card_valores", filter: `id_time=eq.${id_cliente}` },
        (payload) => {
          const row: any = payload.new;
          if (!row || row.id_campo == null) return;
          if (String(row.fone || "").replace(/\D/g, "") !== foneLead) return; // outro lead
          if (dirtyFields.current.has(row.id_campo)) return; // você está editando esse campo
          setValues((prev) => {
            const ex = prev.find((p) => p.id_campo === row.id_campo);
            return ex
              ? prev.map((p) => (p.id_campo === row.id_campo ? { ...p, valor: row.valor } : p))
              : [...prev, { id_campo: row.id_campo, valor: row.valor }];
          });
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [show, leadPhone, idTime]);

  useEffect(() => {
    if (selectedFunnel) {
      fetchStructure(selectedFunnel);
    }
  }, [selectedFunnel]);

  const fetchFunnels = async () => {
    const id_cliente = timeDoCard();
    if (!id_cliente) return;

    // 💾 Cache: Load funnels immediately
    const cacheKey = `card_funnels_${id_cliente}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      try {
        const data = JSON.parse(cached);
        setFunnels(data);
        // Initial match logic for cached data
        const match = data.find((f: Funnel) => f.nome === initialFunnelName);
        if (match) setSelectedFunnel(match.id);
        else if (data.length > 0 && !selectedFunnel) setSelectedFunnel(data[0].id);
      } catch (e) { console.error("Cache funnels error", e); }
    }

    try {
      const res = await fetch(`${API_URL}/card/funnels?id_cliente=${id_cliente}`);
      if (res.ok) {
        const data = await res.json();
        setFunnels(data);
        localStorage.setItem(cacheKey, JSON.stringify(data)); // Update cache
        
        // Try to match initial funnel name to ID
        const match = data.find((f: Funnel) => f.nome === initialFunnelName);
        if (match) {
          setSelectedFunnel(match.id);
        } else if (data.length > 0) {
          // Only override if not set (preserve user selection if they were fast)
          setSelectedFunnel(prev => prev || data[0].id);
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchStructure = async (funnelId: number) => {
    const id_cliente = timeDoCard();
    if (!id_cliente) return;

    // 💾 Cache: Structure
    const cacheKey = `card_structure_${id_cliente}_${funnelId}`;
    const cached = localStorage.getItem(cacheKey);
    let hasCache = false;

    if (cached) {
      try {
        const data = JSON.parse(cached);
        if (Array.isArray(data) && data.length > 0) {
           setGroups(data);
           hasCache = true;
        }
      } catch (e) { console.error("Cache structure error", e); }
    }

    if (!hasCache) setLoading(true);

    try {
      const res = await fetch(`${API_URL}/card/groups?id_funil_pai=${funnelId}&id_cliente=${id_cliente}`);
      if (res.ok) {
        const groupsData: Group[] = await res.json();
        const groupsWithFields = await Promise.all(groupsData.map(async (g) => {
          const fieldsRes = await fetch(`${API_URL}/card/fields?id_grupo=${g.id}`);
          const fields = fieldsRes.ok ? await fieldsRes.json() : [];
          return { ...g, fields };
        }));
        setGroups(groupsWithFields);
        localStorage.setItem(cacheKey, JSON.stringify(groupsWithFields));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchValues = async () => {
    const id_cliente = timeDoCard();
    if (!id_cliente) return;

    // 💾 Cache: Values
    const cacheKey = `card_values_${id_cliente}_${leadPhone}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      try {
        mergePreservandoDirty(JSON.parse(cached));
      } catch (e) { console.error("Cache values error", e); }
    }

    try {
      // fone must be sent exactly as stored. Ensure consistency.
      const res = await fetch(`${API_URL}/card/values?id_cliente=${id_cliente}&fone=${leadPhone}`);
      if (res.ok) {
        const data = await res.json();
        mergePreservandoDirty(data); // não sobrescreve o que o usuário está digitando
        localStorage.setItem(cacheKey, JSON.stringify(data));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleValueChange = (fieldId: number, newValue: string) => {
    dirtyFields.current.add(fieldId); // protege de ser sobrescrito por cargas do servidor
    setValues(prev => {
      const existing = prev.find(v => v.id_campo === fieldId);
      let updated: CardValue[];
      if (existing) {
        updated = prev.map(v => v.id_campo === fieldId ? { ...v, valor: newValue } : v);
      } else {
        updated = [...prev, { id_campo: fieldId, valor: newValue }];
      }
      return updated;
    });
  };

  const saveValueToBackend = async (fieldId: number, value: string) => {
    const id_cliente = timeDoCard();
    if (!id_cliente) return;

    // 💾 Update cache immediately on save
    const cacheKey = `card_values_${id_cliente}_${leadPhone}`;
    // We rely on 'values' state being mostly up to date, but here we construct the single change
    // Since state update is async, we read from localStorage or helper if needed, 
    // but better is to trust that next render will persist state if we added persistence logic.
    // However, simplest is to update cache with current state + new value.
    // We can't access 'values' perfectly here inside async without ref or dependency.
    // Let's rely on fetchValues refreshing it or `handleBlur` flow.
    // Actually, let's update the cache manually here reading the *current* cache to be safe?
    // No, `handleValueChange` updates the UI state. We should update the cache in `handleValueChange` 
    // or useEffect. But `saveValueToBackend` is fire-and-forget.
    // Let's add a useEffect to sync `values` to localStorage whenever it changes?
    // That might be too aggressive. Let's just update here.
    
    // To keep it simple and robust:
    // We will trust `fetchValues` loop or optimistic updates. 
    // Let's add a specific cache update for this field in localStorage.
    try {
       const cached = localStorage.getItem(cacheKey);
       let currentCache = cached ? JSON.parse(cached) : [];
       const existsIdx = currentCache.findIndex((v: CardValue) => v.id_campo === fieldId);
       if (existsIdx >= 0) {
           currentCache[existsIdx].valor = value;
       } else {
           currentCache.push({ id_campo: fieldId, valor: value });
       }
       localStorage.setItem(cacheKey, JSON.stringify(currentCache));
    } catch(e) { /* ignore */ }

    try {
      await fetch(`${API_URL}/card/values`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id_cliente,
          fone: leadPhone,
          id_campo: fieldId,
          valor: value
        })
      });
    } catch (err) {
      console.error("Error saving value:", err);
    }
  };

  const handleBlur = (fieldId: number, value: string) => {
    saveValueToBackend(fieldId, value);
  };

  // Botão Salvar do rodapé: grava os campos que o usuário tocou nesta abertura
  // (inclusive o que ainda está em edição, que não passou pelo blur) e fecha.
  const salvarEFechar = () => {
    values.forEach((v) => {
      if (dirtyFields.current.has(v.id_campo)) saveValueToBackend(v.id_campo, v.valor);
    });
    setEditingField(null);
    onHide();
  };

  // Auto-fill dos grupos padrão (Nome/Fone). Usa o callback de setValues (estado
  // SEMPRE fresco), só preenche campos VAZIOS e NUNCA toca em campo que o usuário
  // está editando (dirtyFields) — assim não apaga digitação em andamento.
  useEffect(() => {
    if (!show || groups.length === 0 || loading) return;

    const aPreencher: { id: number; valor: string }[] = [];
    setValues((prev) => {
      const out = [...prev];
      for (const group of groups.filter((g) => g.padrao)) {
        if (!group.fields) continue;
        for (const field of group.fields) {
          if (dirtyFields.current.has(field.id)) continue; // usuário está editando
          const existing = out.find((v) => v.id_campo === field.id);
          if (existing?.valor) continue; // já tem valor
          let novo = "";
          const lbl = field.label.toLowerCase();
          if (lbl === "nome" || lbl.includes("nome do cliente")) {
            novo = leadName;
          } else if (lbl === "fone" || lbl === "telefone" || lbl.includes("whatsapp")) {
            let phone = String(leadPhone || "").replace(/\D/g, "");
            if (phone.length >= 10 && phone.length <= 11) phone = `55${phone}`;
            novo = phone;
          }
          if (novo) {
            if (existing) existing.valor = novo;
            else out.push({ id_campo: field.id, valor: novo });
            aPreencher.push({ id: field.id, valor: novo });
          }
        }
      }
      return out;
    });
    // Salva no banco os que foram auto-preenchidos (silencioso)
    aPreencher.forEach((p) => saveValueToBackend(p.id, p.valor));

  }, [groups, show, loading]); // Dependências controladas

  const getValue = (fieldId: number) => {
    return values.find(v => v.id_campo === fieldId)?.valor || "";
  };

  const handleFieldClick = (fieldId: number) => {
    if (!fillingMode) return;

    let textToFill = "";
    if (fillingMode === 'name') {
      textToFill = leadName;
    } else if (fillingMode === 'phone') {
      let phone = String(leadPhone || "").replace(/\D/g, "");
      // Se tiver entre 10 e 11 dígitos, provavelmente falta o DDI 55
      if (phone.length >= 10 && phone.length <= 11) {
        phone = `55${phone}`;
      }
      textToFill = phone;
    }

    // Atualiza estado e salva
    handleValueChange(fieldId, textToFill);
    handleBlur(fieldId, textToFill);
    
    // Sai do modo de preenchimento
    setFillingMode(null);
  };

  // Editor inline (modo edição): sem borda, salva e sai no blur.
  const renderEditor = (field: Field) => {
    const val = getValue(field.id);
    const sairESalvar = (e: any) => { handleBlur(field.id, e.target.value); setEditingField(null); };
    const baseCls = "border-0 px-0 shadow-none bg-transparent";
    const baseStyle: any = { fontSize: 14 };
    if (field.tipo === 'select') {
      const options = field.opcoes ? field.opcoes.split(',').map(o => o.trim()) : [];
      return (
        <Form.Select autoFocus value={val} className={baseCls} style={baseStyle}
          onChange={(e) => handleValueChange(field.id, e.target.value)} onBlur={sairESalvar}>
          <option value="">Selecione...</option>
          {options.map((opt, idx) => <option key={idx} value={opt}>{opt}</option>)}
        </Form.Select>
      );
    }
    if (field.tipo === 'number' || field.tipo === 'currency') {
      return <Form.Control type="number" autoFocus value={val} className={baseCls} style={baseStyle}
        onChange={(e) => handleValueChange(field.id, e.target.value)} onBlur={sairESalvar} />;
    }
    if (field.tipo === 'date') {
      return <Form.Control type="date" autoFocus value={val} className={baseCls} style={baseStyle}
        onChange={(e) => handleValueChange(field.id, e.target.value)} onBlur={sairESalvar} />;
    }
    // text / textarea / default: textarea SEM borda que cresce com o conteúdo
    return (
      <Form.Control as="textarea" rows={1} autoFocus value={val} className={baseCls}
        style={{ ...baseStyle, resize: 'none', overflow: 'hidden', lineHeight: 1.5 }}
        onChange={(e) => {
          handleValueChange(field.id, e.target.value);
          const t = e.target as HTMLTextAreaElement; t.style.height = 'auto'; t.style.height = `${t.scrollHeight}px`;
        }}
        onFocus={(e) => { const t = e.target as HTMLTextAreaElement; t.style.height = 'auto'; t.style.height = `${t.scrollHeight}px`; }}
        onBlur={sairESalvar} />
    );
  };

  // Linha de campo: label (cinza) + valor como TEXTO puro (sem borda). Clicar = editar inline.
  // Checklist: vários itens (de `opcoes`) cada um com uma checkbox. O valor é gravado
  // como JSON array dos itens marcados em card_valores.valor.
  const renderChecklist = (field: Field) => {
    const opts = (field.opcoes || "").split(",").map((o) => o.trim()).filter(Boolean);
    let marcados: string[] = [];
    try { const p = JSON.parse(getValue(field.id) || "[]"); if (Array.isArray(p)) marcados = p; } catch { marcados = []; }
    const toggle = (opt: string) => {
      const novo = marcados.includes(opt) ? marcados.filter((m) => m !== opt) : [...marcados, opt];
      const json = JSON.stringify(novo);
      handleValueChange(field.id, json);
      handleBlur(field.id, json);
    };
    // Ordem canônica de `opcoes` — nunca reordenar por status: a operadora
    // decora a posição do item na lista.
    return (
      <div className="dr-docs">
        {opts.length === 0 ? (
          <span className="text-muted fst-italic" style={{ fontSize: 14 }}>Sem itens configurados</span>
        ) : (
          opts.map((opt, i) => {
            // verde = fato consumado (já marcado); tracejado = ausência (ainda falta)
            const on = marcados.includes(opt);
            return (
              <label key={i} className={`dr-doc ${on ? "dr-ok" : "dr-falta"}`}>
                <input
                  type="checkbox"
                  id={`chk-${field.id}-${i}`}
                  checked={on}
                  onChange={() => toggle(opt)}
                />
                <span className="dr-caixa">
                  {on && (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" style={{ width: 12, height: 12 }}>
                      <path d="m4 12 5.5 5.5L20 7" />
                    </svg>
                  )}
                </span>
                <span className="dr-nome">{opt}</span>
              </label>
            );
          })
        )}
      </div>
    );
  };

  // Itens de checklist ainda não marcados, em todos os grupos do card.
  // Alimenta a promessa: é exatamente "o que ainda falta este cliente enviar".
  const coletarChecklist = () => {
    const pendentes: string[] = [];
    let recebidos = 0;
    groups.forEach((group) => {
      (group.fields || []).forEach((field) => {
        if (field.tipo !== "checklist") return;
        const opts = (field.opcoes || "").split(",").map((o) => o.trim()).filter(Boolean);
        let marcados: string[] = [];
        try {
          const p = JSON.parse(getValue(field.id) || "[]");
          if (Array.isArray(p)) marcados = p;
        } catch { marcados = []; }
        opts.forEach((opt) => {
          if (marcados.includes(opt)) recebidos += 1;
          else pendentes.push(opt);
        });
      });
    });
    return { pendentes, recebidos };
  };

  const renderFieldRow = (field: Field) => {
    const val = getValue(field.id);
    const editando = editingField === field.id;
    if (field.tipo === "checklist") {
      return (
        <div key={field.id} className="mb-3">
          <div className="fs-12 text-muted mb-1">
            {field.label}{field.obrigatorio && <span className="text-danger ms-1">*</span>}
          </div>
          {renderChecklist(field)}
        </div>
      );
    }
    return (
      <div key={field.id} className="mb-3">
        <div className="fs-12 text-muted mb-1">
          {field.label}{field.obrigatorio && <span className="text-danger ms-1">*</span>}
        </div>
        {editando ? renderEditor(field) : (
          <div
            onClick={() => (fillingMode ? handleFieldClick(field.id) : setEditingField(field.id))}
            className={val ? "text-dark" : "text-muted fst-italic"}
            style={{ cursor: fillingMode ? 'copy' : 'text', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 14, minHeight: 21 }}
          >
            {val || "Clique para preencher"}
          </div>
        )}
      </div>
    );
  };

  const standardGroups = groups.filter(g => g.padrao);
  const otherGroups = groups.filter(g => !g.padrao);
  const allGroups = [...standardGroups, ...otherGroups];

  return (
    <>
    <Modal
      show={show}
      onHide={onHide}
      centered
      scrollable
      className="modern-modal"
      dialogClassName="dr-dialog-660"
      contentClassName="dr-escopo dr-compacto dr-conteudo"
    >
      {/* Cabeçalho no padrão dos mockups de retenção: selo + título + subtítulo */}
      <div className="dr-topo">
        <div className="dr-selo dr-selo-coral">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 21, height: 21 }}>
            <rect x="3" y="4" width="18" height="16" rx="2" />
            <path d="M3 10h18M8 15h8" />
          </svg>
        </div>
        <div>
          <h2>{leadName || "Lead sem Nome"}</h2>
          <p>{leadPhone}</p>
        </div>
        <button type="button" className="dr-fechar" aria-label="Fechar" onClick={onHide}>✕</button>
      </div>

      <Modal.Body className="dr-corpo">
        {/* CATEGORIAS: funil selecionado como botão teal */}
        <div className="mb-3">
          <div className="fs-12 fw-bold text-uppercase text-muted mb-2" style={{ letterSpacing: 1 }}>
            Categorias
          </div>
          <Form.Select
            value={selectedFunnel || ""}
            onChange={(e) => setSelectedFunnel(Number(e.target.value))}
            className="fw-semibold border-0"
            style={{ backgroundColor: '#0d9aad', color: '#fff', borderRadius: 8, height: 42 }}
          >
            {funnels.map(f => (
              <option key={f.id} value={f.id} style={{ color: '#000', backgroundColor: '#fff' }}>{f.nome}</option>
            ))}
          </Form.Select>
        </div>

        {loading ? (
          <div className="text-center p-5">
            <div className="spinner-border text-primary mb-2" role="status"></div>
            <div className="text-muted">Sincronizando dados do card...</div>
          </div>
        ) : allGroups.length === 0 ? (
          <div className="text-center p-5 border-dashed rounded-4 bg-light">
            <div className="mb-3 text-muted">
              <i className="ti ti-layout-board-split fs-40"></i>
            </div>
            <h6 className="fw-bold">Nenhum card configurado</h6>
            <p className="text-muted small mb-3">Este funil ainda não possui campos definidos para o card.</p>
            <Button variant="primary" className="rounded-pill px-4" href="/card-builder">
              Configurar Agora
            </Button>
          </div>
        ) : (
          <div>
            {allGroups.map(group => (
              <div key={group.id} className="mb-4">
                <h6 className="fw-bold mb-3" style={{ color: '#e8590c' }}>{group.nome}</h6>
                <div className="row">
                  {group.fields && group.fields.map(field => (
                    <div key={field.id} className="col-md-6">
                      {renderFieldRow(field)}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
      {/* Um botão só: o card grava sozinho (checklist no clique, campos no blur), então
          "fechar" e "salvar" são a mesma ação — o rótulo diz isso em voz alta para a
          operadora não ficar procurando um Salvar que nunca existiu de verdade. */}
      <div className="dr-rodape">
        <button type="button" className="dr-btn-salvar" onClick={salvarEFechar}>
          Fechar (Salvar)
        </button>
      </div>
    </Modal>

    {/* Expansão de campo de texto: vê/edita o conteúdo completo em tela cheia */}
    <Modal
      show={!!expandedField}
      onHide={() => setExpandedField(null)}
      centered
      backdropClassName="modal-backdrop-stacked"
      style={{ zIndex: 1060 }}
    >
      <Modal.Header closeButton className="border-0 pb-1">
        <Modal.Title className="fs-6 fw-bold d-flex align-items-center gap-2">
          <i className="ti ti-id text-primary" />
          {expandedField?.label || "Campo"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-2">
        {expandedField && (
          <Form.Control
            as="textarea"
            rows={10}
            autoFocus
            value={getValue(expandedField.id)}
            onChange={(e) => handleValueChange(expandedField.id, e.target.value)}
            onBlur={(e) => handleBlur(expandedField.id, e.target.value)}
            placeholder={expandedField.label}
            style={{ fontSize: 14, lineHeight: 1.6 }}
          />
        )}
      </Modal.Body>
      <Modal.Footer className="border-0 pt-0">
        <Button
          variant="light"
          className="rounded-pill px-4 fw-medium"
          onClick={() => {
            if (expandedField) handleBlur(expandedField.id, getValue(expandedField.id));
            setExpandedField(null);
          }}
        >
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
    </>
  );
};

export default CardViewModal;
